/**
 * The "new layout" tour: who sees it, what it walks, and the week of gold
 * dots after it. Pure, so the rules are tested without rendering anything.
 *
 * Coleman, 2026-10-07: all three pieces. A card on the first open after the
 * new sidebar shipped, "Show me" walking four stops on the real screen, and
 * for a week a gold dot on each thing that moved until it has been opened.
 *
 * Only for coaches who knew the old layout: anyone who joined after it
 * shipped gets the ordinary "What's here" tour, which already shows the new
 * one. Once per account (user_data), not per device.
 */

/** When the new sidebar went live (the merge to main, df195a7). Accounts
 *  made after this never saw the old one. */
export const LAYOUT_RELEASED_AT = "2026-10-07T10:26:52Z";
/** How long a coach must have had the old layout to be told it changed.
 *  Someone in their first day hadn't learned it yet; the ordinary "What's
 *  here" tour is the right thing for them (Coleman, 2026-10-07: new coaches
 *  get the updated tour, not "We've made things a bit easier"). */
export const MIN_TENURE_MS = 24 * 3600 * 1000;
/** The user_data key, and the localStorage mirror that answers before it loads. */
export const LAYOUT_TOUR_KEY = "layout_tour_2026_10";
/** How long the gold dots stay after the coach first sees the card. */
export const PULSE_DAYS = 7;

/** The things that moved, by the data-tour-target they carry. */
export const MOVED = ["nav", "live", "profile", "learn"];

/** Desktop stops. */
export const DESKTOP_STEPS = [
  { target: "nav",     title: "Five things down the side",
    body: "Learn, Coach and the Drill Library fold open to show what's inside. Learn also has its own page." },
  { target: "live",    title: "Calls and Chats live here now",
    body: "When the next call is, and how many messages are waiting, always at the bottom of the sidebar." },
  { target: "profile", title: "Your stuff is under your name",
    body: "My Portal, What's here and Submit Content are in the menu on this card." },
  { target: "learn",   title: "Learn has its own page",
    body: "Today's session, what's just dropped, and the masterclasses you left halfway." },
];

/** Phone stops. */
export const PHONE_STEPS = [
  { target: "tabs",    title: "Five tabs at the bottom",
    body: "Home, Community, Learn, Drills and Coach. Drills and Coach open a list of what's inside." },
  { target: "chats",   title: "Chats is up here now",
    body: "Next to the bell, with how many messages are waiting. Calls are in the menu." },
  { target: "menu",    title: "My Portal is in the menu",
    body: "Open the menu: My Portal, What's here and Submit Content are under You, with Calls just above." },
  { target: "learn-tab", title: "Learn has its own page",
    body: "Today's session, what's just dropped, and the masterclasses you left halfway." },
];

/** Whether this account should get the card at all. */
export function tourEligible(joinedAt) {
  const j = new Date(joinedAt).getTime();
  const r = new Date(LAYOUT_RELEASED_AT).getTime();
  return Number.isFinite(j) && j <= r - MIN_TENURE_MS;
}

/** The card shows once: eligible, never seen, and not skipped because the
 *  coach took the "What's here" tour, which already shows the new layout. */
export function shouldShowCard(joinedAt, state) {
  return tourEligible(joinedAt) && !(state && (state.seenAt || state.skipped));
}

/** Which gold dots are still on: a week from first seeing the card, and
 *  only on the things not yet opened. */
export function activePulses(state, now = Date.now()) {
  if (!state || !state.seenAt || state.skipped) return [];
  const seen = new Date(state.seenAt).getTime();
  if (!Number.isFinite(seen) || now - seen > PULSE_DAYS * 86400000 || now < seen) return [];
  const opened = new Set(state.opened || []);
  return MOVED.filter(id => !opened.has(id));
}

/** Which moved thing a page visit counts as opening. */
export function openedBy(nav) {
  if (typeof nav !== "string") return null;
  const base = nav.split("/")[0];
  if (base === "chats" || base === "calls") return "live";
  if (base === "learn-home") return "learn";
  if (["pd", "team", "workouts", "movement", "pro"].includes(base)) return "nav";
  return null;
}
